/**
 * This util holds a centralized location for all sticky elements and helpers
 * to assist in maintaining list of such elements. The sticky stack aims to keep
 * sticky implementations DRY without having each sticky element needing to set
 * top offsets manually. Unfortunately, we are unable to utilize this with pure
 * CSS because that would require each position sticky element to be in the same
 * container, which we do not have the luxury of having.
 *
 * This will also leverage intersection observers and apply a class of `stuck` if
 * there is an `is-sticky` class on the element.
 *
 */

const stickyStack = new Set<HTMLElement>();
const elementToIntersectionObserverMap = new Map<HTMLElement, {value: IntersectionObserver; top: number}>();
let alignRafId;

/**
 * The actual hooks that get attached to the specific element in which sticky is positioned.
 * Example:
 *  .element(
      hook=($helpers.isSticky ? $helpers.stickyElemHooks : {})
    )
 * Sometimes in different browsers, if your sticky element is the only component that renders
 * (outside of the chrome header if applicable), your element will be inserted before it is
 * initialized or loaded. Therefore, you'll have to check whether your component is initialized before
 * you add in the sticky hooks, such as the following example:
 *  .element(
      hook=($helpers.isInitialized() ? $helpers.stickyElemHooks : {})
    )
 * where isInitialized points to this.initialized in your javascript file
 * you might instead choose to check whether it is fully loaded (with whatever attribute you have)
 */
export const stickyElemHooks = {
  insert: ({elm}) => insertStickyElement(elm),
  // take care of the case where element needs to be inserted to stack after it's already been inserted to the DOM
  update: ({elm}) => insertStickyElement(elm),
  destroy: ({elm}) => removeStickyElement(elm),
};

function cancelAndRequestAlignment() {
  cancelAnimationFrame(alignRafId);
  alignRafId = requestAnimationFrame(() => alignStickyStack());
}

/**
 * Adds HTMLElement from snabbdom hook to be aligned. Should add
 * in the order it was received.
 *
 * @param {HTMLElement} elm element to add
 */
function insertStickyElement(elm: HTMLElement) {
  stickyStack.add(elm);
  cancelAndRequestAlignment();
}

/**
 * Removes HTMLElement element from stack
 *
 * @param {HTMLElement} elm element to remove
 */
function removeStickyElement(elm: HTMLElement) {
  if (elementToIntersectionObserverMap.has(elm)) {
    const intersectionObserver = elementToIntersectionObserverMap.get(elm);
    intersectionObserver.value.unobserve(elm);
    elementToIntersectionObserverMap.delete(elm);
  }

  stickyStack.delete(elm);
  cancelAndRequestAlignment();
}

/**
 * Takes all the sticky elements and calculates its top positions based on
 * the height of each element, thus stacking them top of each other nicely
 */
function alignStickyStack() {
  let topOffset = 0;
  for (const stickyElement of stickyStack) {
    stickyElement.style.top = `${topOffset}px`;
    if (stickyElement.classList.contains(`is-sticky`)) {
      setupIntersectionObserver(stickyElement, topOffset);
    }
    topOffset += stickyElement.offsetHeight;
  }
}

/**
 * Setup intersection observers for the sticky elements. This will apply a `stuck` class
 * to enable styling when the item is in the sticky position
 *
 * @param elm element with sticky positioning that will have a `stuck` class applied
 * @param top stickiness top value
 */
function setupIntersectionObserver(elm: HTMLElement, top: number) {
  if (elementToIntersectionObserverMap.has(elm)) {
    const existingObserver = elementToIntersectionObserverMap.get(elm);

    // existing top value matches what we're about to configure so lets bail and leave it as is
    if (existingObserver.top === top) {
      return;
    }

    // remove existing observer
    existingObserver.value.unobserve(elm);
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const isStuck = entry.target.getBoundingClientRect().top === top;
        entry.target.classList.toggle(`stuck`, isStuck);
      });
    },
    {
      // specifying null, takes the top of the viewport. All calculations assume we're basing everything
      // on the top of the viewport
      root: null,
      // give us one pixel of overlap to track and trigger the observer since we don't ever actually
      // allow the object to overlap, an alternative pattern would be to use sentinels without the 1px
      // but that would require more DOM nodes and may have unexpected impacts (ie. nth-child styling)
      rootMargin: `-${top + 1}px 0px 0px 0px`,
      // Only triggering at [0, 1] causes inconsistencies as elements go above and below their sticking
      // point. These extra thresholds ensure for consistent behavior
      threshold: [0, 0.1, 0.9, 1],
    },
  );
  observer.observe(elm);
  elementToIntersectionObserverMap.set(elm, {value: observer, top});
}
